'use client';

import React, { type FC, type ReactNode, useState } from 'react';
import { User01Icon } from '@milanote/roma-icons';
import { cx } from '../../../utils/cx';
import { AvatarOnlineIndicator, VerifiedTick } from './baseComponents';

const SEMANTIC_NAME = 'RomaAvatar';

export type AvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/**
 * Rendered pixel dimensions of each semantic size, kept in sync with the `styles.sizes` root classes.
 * Callers resolving their own images use this so requested image quality tracks the rendered size.
 */
export const AVATAR_SIZE_PX: Record<AvatarSize, number> = {
    xxs: 16,
    xs: 24,
    sm: 32,
    md: 40,
    lg: 48,
    xl: 56,
    '2xl': 64,
};

export interface AvatarProps {
    size?: AvatarSize;
    className?: string;
    src?: string | null;
    alt?: string;
    /**
     * Optional background color for avatar.
     */
    color?: 'blue' | 'green' | 'orange' | 'pink' | 'purple' | 'red' | 'teal' | 'yellow' | string;
    /**
     * Display a contrast border around the avatar.
     */
    contrastBorder?: boolean;
    /**
     * Display a badge (i.e. company logo).
     */
    badge?: ReactNode;
    /**
     * Display a status indicator.
     */
    status?: 'online' | 'offline';
    /**
     * Display a verified tick icon.
     *
     * @default false
     */
    verified?: boolean;

    /**
     * The initials of the user to display if no image is available.
     */
    initials?: string;
    /**
     * An icon to display if no image is available.
     */
    placeholderIcon?: FC<{ className?: string }>;
    /**
     * A placeholder to display if no image is available.
     */
    placeholder?: ReactNode;

    /**
     * Whether the avatar should show a focus ring when the parent group is in focus.
     * For example, when the avatar is wrapped inside a link.
     *
     * @default false
     */
    focusable?: boolean;
}

const styles = {
    sizes: {
        xxs: {
            root: 'size-4 outline-[0.5px] -outline-offset-[0.5px]',
            initials: 'text-[0.7rem] font-semibold',
            icon: 'size-3',
            badge: 'xs',
            badgeVerifiedTick: '-right-px -bottom-px',
        },
        xs: {
            root: 'size-6 outline-[0.5px] -outline-offset-[0.5px]',
            initials: 'text-xs font-semibold',
            icon: 'size-4',
            badge: 'xs',
            badgeVerifiedTick: '-right-px -bottom-px',
        },
        sm: {
            root: 'size-8 outline-[0.75px] -outline-offset-[0.75px]',
            initials: 'text-sm font-semibold',
            icon: 'size-5',
            badge: 'sm',
            badgeVerifiedTick: '',
        },
        md: {
            root: 'size-10 outline-1 -outline-offset-1',
            initials: 'text-md font-semibold',
            icon: 'size-6',
            badge: 'md',
            badgeVerifiedTick: '',
        },
        lg: {
            root: 'size-12 outline-1 -outline-offset-1',
            initials: 'text-lg font-semibold',
            icon: 'size-7',
            badge: 'lg',
            badgeVerifiedTick: '',
        },
        xl: {
            root: 'size-14 outline-1 -outline-offset-1',
            initials: 'text-display-md font-semibold',
            icon: 'size-8',
            badge: 'xl',
            badgeVerifiedTick: '',
        },
        '2xl': {
            root: 'size-16 outline-1 -outline-offset-1',
            initials: 'text-display-xl font-semibold',
            icon: 'size-8',
            badge: '2xl',
            badgeVerifiedTick: '',
        },
    },
    colors: {
        default: 'text-secondary bg-avatar-bg',
        blue: 'text-black bg-avatar-blue',
        green: 'text-black bg-avatar-green',
        orange: 'text-black bg-avatar-orange',
        pink: 'text-black bg-avatar-pink',
        purple: 'text-black bg-avatar-purple',
        red: 'text-black bg-avatar-red',
        teal: 'text-black bg-avatar-teal',
        yellow: 'text-black bg-avatar-yellow',
    },
} as const;

type AvatarMainContentProps = AvatarProps & {
    isFailed: boolean;
    onImageError: () => void;
};

const AvatarMain: FC<AvatarMainContentProps> = ({
    src,
    alt,
    isFailed,
    onImageError,
    initials,
    placeholder,
    placeholderIcon: PlaceholderIcon,
    size = 'md',
}) => {
    if (src && !isFailed) {
        return (
            <img
                data-avatar-img
                className="size-full rounded-full object-cover"
                src={src}
                alt={alt}
                onError={onImageError}
            />
        );
    }

    if (initials) {
        return <span className={cx(styles.sizes[size].initials)}>{initials}</span>;
    }

    if (PlaceholderIcon) {
        return <PlaceholderIcon className={cx('text-secondary', styles.sizes[size].icon)} />;
    }

    if (placeholder) {
        return <>{placeholder}</>;
    }

    return <User01Icon className={cx('text-secondary', styles.sizes[size].icon)} />;
};

type AvatarBadgeProps = AvatarProps & {
    size: AvatarSize;
};

const AvatarBadge: FC<AvatarBadgeProps> = ({ status, verified, badge, size = 'md' }) => {
    const badgeSize = styles.sizes[size].badge;

    if (status) {
        return <AvatarOnlineIndicator status={status} size={badgeSize} />;
    }

    if (verified) {
        return (
            <VerifiedTick
                size={badgeSize}
                className={cx('absolute right-0 bottom-0', styles.sizes[size].badgeVerifiedTick)}
            />
        );
    }

    return <>{badge}</>;
};

export const Avatar = ({
    contrastBorder = true,
    size = 'md',
    src,
    alt,
    initials,
    placeholder,
    placeholderIcon: PlaceholderIcon,
    badge,
    status,
    verified,
    focusable = false,
    className,
    color,
}: AvatarProps) => {
    // Tracking which source failed (rather than a boolean) means a replaced source is retried straight away.
    const [failedSrc, setFailedSrc] = useState<string | null>(null);

    return (
        <div
            data-avatar
            className={cx(
                SEMANTIC_NAME,
                'relative inline-flex shrink-0 items-center justify-center rounded-full outline-transparent',
                // apply supplied color class or fall back to default background
                styles.colors[color as keyof typeof styles.colors] || styles.colors.default,
                // Focus styles
                focusable &&
                    'group-outline-focus-ring group-focus-visible:outline-2 group-focus-visible:-outline-offset-1',
                contrastBorder && 'outline outline-avatar-contrast-border',
                styles.sizes[size].root,
                className,
            )}
        >
            <AvatarMain
                src={src}
                alt={alt}
                isFailed={src === failedSrc}
                onImageError={() => setFailedSrc(src ?? null)}
                initials={initials}
                placeholder={placeholder}
                placeholderIcon={PlaceholderIcon}
                size={size}
            />
            <AvatarBadge status={status} verified={verified} badge={badge} size={size} />
        </div>
    );
};

Avatar.displayName = SEMANTIC_NAME;
